import { useState, useRef, useCallback, useMemo } from 'react';
import type { ChangeEvent, CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import {
  Textarea,
  Popover,
  Stack,
  Text,
  UnstyledButton,
  Box,
  Group,
  Badge,
  Menu,
  ActionIcon,
  Loader,
} from '@mantine/core';
import { IconVariable, IconSpeakerphone, IconChevronDown, IconLanguage } from '@tabler/icons-react';
import { speechFormats, speechFormatCategories } from '@legacy-shared/texml/speechFormats';
import type { SpeechFormatCategory } from '@legacy-shared/texml/speechFormats';
import type { VariableInfo, LanguageOption } from '../../widgets/types';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/use-org';

function getCaretOffset(textarea: HTMLTextAreaElement, position: number): { top: number; left: number } {
  const div = document.createElement('div');
  const style = window.getComputedStyle(textarea);
  const properties = [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'lineHeight',
    'letterSpacing',
    'wordSpacing',
    'textTransform',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'boxSizing',
  ] as const;
  for (const prop of properties) {
    div.style.setProperty(prop.replace(/([A-Z])/g, '-$1').toLowerCase(), style[prop]);
  }
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.wordWrap = 'break-word';
  div.style.overflow = 'hidden';
  div.style.width = `${textarea.offsetWidth}px`;
  div.style.height = 'auto';

  const text = textarea.value.substring(0, position);
  div.textContent = text;

  const span = document.createElement('span');
  span.textContent = textarea.value.substring(position) || '.';
  div.appendChild(span);

  document.body.appendChild(div);
  const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.55;
  const top = span.offsetTop + lineHeight - textarea.scrollTop - textarea.offsetHeight;
  const left = span.offsetLeft;
  document.body.removeChild(div);

  return { top, left };
}

const categoryLabels: Record<SpeechFormatCategory, string> = {
  number: 'Numbers',
  text: 'Text',
  datetime: 'Date & Time',
  contact: 'Contact',
};

type DropdownMode = 'variable' | 'speechFormat' | null;

type TriggerContext = {
  mode: 'variable' | 'speechFormat';
  triggerPosition: number;
  filterText: string;
};

function detectTriggerContext(text: string, caretPosition: number): TriggerContext | null {
  const before = text.substring(0, caretPosition);

  const variableStart = before.lastIndexOf('{{');
  if (variableStart !== -1) {
    const typed = before.substring(variableStart + 2);
    if (/^\w*$/.test(typed)) {
      return { mode: 'variable', triggerPosition: variableStart, filterText: typed };
    }
  }

  const slashStart = before.lastIndexOf('/');
  if (slashStart !== -1) {
    const charBefore = slashStart > 0 ? before[slashStart - 1] : undefined;
    if (charBefore === undefined || charBefore === ' ' || charBefore === '\n') {
      const typed = before.substring(slashStart + 1);
      if (/^\w*$/.test(typed)) {
        return { mode: 'speechFormat', triggerPosition: slashStart, filterText: typed };
      }
    }
  }

  return null;
}

type VariableTextareaProps = {
  value: string | Record<string, string>;
  onChange: (value: string | Record<string, string>) => void;
  availableVariables?: VariableInfo[];
  languages?: LanguageOption[];
  defaultLanguage?: string;
  label?: ReactNode;
  description?: string;
  placeholder?: string;
  minRows?: number;
  style?: CSSProperties;
  styles?: Record<string, CSSProperties>;
};

function getTextForLanguage(value: string | Record<string, string>, languageCode: string | undefined): string {
  if (typeof value === 'string') return value;
  if (!languageCode) return Object.values(value)[0] ?? '';
  return value[languageCode] ?? '';
}

function setTextForLanguage(
  value: string | Record<string, string>,
  languageCode: string | undefined,
  text: string,
  hasLanguages: boolean,
): string | Record<string, string> {
  if (!hasLanguages || !languageCode) return text;
  const record = typeof value === 'string' ? {} : { ...value };
  record[languageCode] = text;
  return record;
}

export function VariableTextarea({
  value,
  onChange,
  availableVariables = [],
  languages,
  defaultLanguage,
  label,
  description,
  placeholder,
  minRows = 3,
  style,
  styles,
}: VariableTextareaProps) {
  const { organizationId } = useOrg();
  const hasLanguages = !!languages && languages.length > 0;
  const [selectedLanguage, setSelectedLanguage] = useState<string>(defaultLanguage ?? languages?.[0]?.code ?? '');
  const [dropdownMode, setDropdownMode] = useState<DropdownMode>(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [triggerPosition, setTriggerPosition] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [caretOffset, setCaretOffset] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dismissedTrigger = useRef<{ mode: 'variable' | 'speechFormat'; triggerPosition: number } | null>(null);

  const currentText = hasLanguages
    ? getTextForLanguage(value, selectedLanguage)
    : typeof value === 'string'
      ? value
      : (Object.values(value)[0] ?? '');

  const handleTextChange = useCallback(
    (text: string) => {
      onChange(setTextForLanguage(value, selectedLanguage, text, hasLanguages));
    },
    [onChange, value, selectedLanguage, hasLanguages],
  );

  const syncTriggerContext = useCallback(
    (textarea: HTMLTextAreaElement) => {
      const pos = textarea.selectionStart;
      setCursorPosition(pos);

      const context = detectTriggerContext(textarea.value, pos);

      if (!context || (context.mode === 'variable' && availableVariables.length === 0)) {
        dismissedTrigger.current = null;
        setDropdownMode(null);
        return;
      }

      const dismissed = dismissedTrigger.current;
      if (dismissed && dismissed.mode === context.mode && dismissed.triggerPosition === context.triggerPosition) {
        return;
      }
      dismissedTrigger.current = null;

      setTriggerPosition(context.triggerPosition);
      setFilterText(context.filterText);
      setCaretOffset(getCaretOffset(textarea, context.triggerPosition));
      setDropdownMode(context.mode);
    },
    [availableVariables.length],
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        const context = detectTriggerContext(e.currentTarget.value, e.currentTarget.selectionStart);
        if (context) dismissedTrigger.current = { mode: context.mode, triggerPosition: context.triggerPosition };
        setDropdownMode(null);
        return;
      }
      syncTriggerContext(e.currentTarget);
    },
    [syncTriggerContext],
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLTextAreaElement>) => {
      syncTriggerContext(e.currentTarget);
    },
    [syncTriggerContext],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      handleTextChange(e.target.value);
      syncTriggerContext(e.target);
    },
    [handleTextChange, syncTriggerContext],
  );

  const insertTriggerAtCaret = useCallback(
    (mode: 'variable' | 'speechFormat') => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const trigger = mode === 'variable' ? '{{' : '/';
      const needsSpace = mode === 'speechFormat' && start > 0 && !/\s/.test(currentText[start - 1] ?? '');
      const inserted = needsSpace ? ` ${trigger}` : trigger;
      const triggerStart = needsSpace ? start + 1 : start;
      const newPos = start + inserted.length;

      handleTextChange(currentText.substring(0, start) + inserted + currentText.substring(textarea.selectionEnd));
      dismissedTrigger.current = null;
      setCursorPosition(newPos);
      setTriggerPosition(triggerStart);
      setFilterText('');
      setCaretOffset(getCaretOffset(textarea, triggerStart));
      setDropdownMode(mode);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    },
    [currentText, handleTextChange],
  );

  const insertVariable = useCallback(
    (varName: string) => {
      const before = currentText.substring(0, triggerPosition);
      const after = currentText.substring(cursorPosition);
      const insertion = `{{${varName}}}`;
      const newValue = `${before}${insertion}${after}`;
      handleTextChange(newValue);
      setDropdownMode(null);

      setTimeout(() => {
        if (textareaRef.current) {
          const newPos = triggerPosition + insertion.length;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newPos, newPos);
        }
      }, 0);
    },
    [currentText, triggerPosition, cursorPosition, handleTextChange],
  );

  const insertSpeechFormat = useCallback(
    (formatId: string) => {
      const before = currentText.substring(0, triggerPosition);
      const after = currentText.substring(cursorPosition);
      const insertion = `/${formatId}()`;
      const newValue = `${before}${insertion}${after}`;
      handleTextChange(newValue);
      setDropdownMode(null);

      setTimeout(() => {
        if (textareaRef.current) {
          const newPos = triggerPosition + insertion.length - 1;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newPos, newPos);
        }
      }, 0);
    },
    [currentText, triggerPosition, cursorPosition, handleTextChange],
  );

  const filteredVariables = useMemo(() => {
    if (!filterText) return availableVariables;
    const lower = filterText.toLowerCase();
    return availableVariables.filter(
      (v) => v.name.toLowerCase().startsWith(lower) || v.description.toLowerCase().includes(lower),
    );
  }, [filterText, availableVariables]);

  const filteredFormats = useMemo(() => {
    if (!filterText) return speechFormats;
    const lower = filterText.toLowerCase();
    return speechFormats.filter((f) => f.id.toLowerCase().startsWith(lower) || f.label.toLowerCase().startsWith(lower));
  }, [filterText]);

  const groupedFormats = useMemo(
    () =>
      speechFormatCategories
        .map((category) => ({ category, formats: filteredFormats.filter((f) => f.category === category) }))
        .filter((group) => group.formats.length > 0),
    [filteredFormats],
  );

  const isNonDefaultLanguage = hasLanguages && selectedLanguage !== defaultLanguage;
  const defaultLangText = defaultLanguage ? getTextForLanguage(value, defaultLanguage) : '';
  const canTranslate = isNonDefaultLanguage && !currentText && defaultLangText.trim().length > 0;

  const handleTranslate = useCallback(async () => {
    if (!defaultLanguage || !selectedLanguage || !organizationId) return;
    const sourceText = getTextForLanguage(value, defaultLanguage);
    if (!sourceText.trim()) return;

    setIsTranslating(true);

    const { data, error } = await api.ai.translate.post({
      organizationId,
      text: sourceText,
      sourceLanguage: languages?.find((l) => l.code === defaultLanguage)?.label ?? defaultLanguage,
      targetLanguage: languages?.find((l) => l.code === selectedLanguage)?.label ?? selectedLanguage,
    });

    if (!error) handleTextChange(data.text);

    setIsTranslating(false);
  }, [defaultLanguage, selectedLanguage, organizationId, value, languages, handleTextChange]);

  const selectedLangObj = languages?.find((l) => l.code === selectedLanguage);

  const insertButtons = (
    <Group justify="flex-end" gap="sm" mt={4}>
      {availableVariables.length > 0 && (
        <UnstyledButton
          title="Insert a variable (or type {{ in the message)"
          onClick={() => insertTriggerAtCaret('variable')}
        >
          <Group gap={4} wrap="nowrap">
            <IconVariable size={12} style={{ color: 'var(--mantine-color-indigo-5)' }} />
            <Text size="xs" c="dimmed">
              Variable
            </Text>
          </Group>
        </UnstyledButton>
      )}
      <UnstyledButton
        title="Insert speech formatting (or type / in the message)"
        onClick={() => insertTriggerAtCaret('speechFormat')}
      >
        <Group gap={4} wrap="nowrap">
          <IconSpeakerphone size={12} style={{ color: 'var(--mantine-color-violet-5)' }} />
          <Text size="xs" c="dimmed">
            Speech format
          </Text>
        </Group>
      </UnstyledButton>
    </Group>
  );

  const languageLabel = hasLanguages ? (
    <Group gap={4} align="center" wrap="nowrap">
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <UnstyledButton>
            <Group gap={4} align="center" wrap="nowrap">
              <IconLanguage size={14} style={{ color: 'var(--mantine-color-grape-6)' }} />
              <Text component="span" size="sm" fw={500}>
                {label ? <>{label} — </> : null}
                {selectedLangObj?.label ?? selectedLanguage}
              </Text>
              <IconChevronDown size={12} style={{ color: 'var(--mantine-color-dimmed)' }} />
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          {languages?.map((lang) => (
            <Menu.Item
              key={lang.code}
              onClick={() => setSelectedLanguage(lang.code)}
              fw={lang.code === selectedLanguage ? 600 : undefined}
              rightSection={
                lang.code === defaultLanguage ? (
                  <Badge size="xs" variant="light">
                    default
                  </Badge>
                ) : typeof value === 'object' && value[lang.code]?.trim() ? (
                  <Badge size="xs" variant="dot" color="green" />
                ) : null
              }
            >
              {lang.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
      {canTranslate && (
        <ActionIcon
          variant="subtle"
          size="xs"
          color="grape"
          onClick={handleTranslate}
          loading={isTranslating}
          title="Auto-translate from default language"
        >
          <IconLanguage size={12} />
        </ActionIcon>
      )}
      {isTranslating && <Loader size={12} />}
    </Group>
  ) : (
    label
  );

  return (
    <Popover
      opened={dropdownMode !== null}
      position="bottom-start"
      width={300}
      shadow="md"
      offset={{ mainAxis: caretOffset.top, crossAxis: caretOffset.left }}
      middlewares={{ flip: false, shift: true }}
      floatingStrategy="fixed"
      withinPortal
      onClose={() => setDropdownMode(null)}
    >
      <Textarea
        ref={textareaRef}
        label={languageLabel}
        description={description}
        placeholder={placeholder}
        value={currentText}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onClick={handleClick}
        minRows={minRows}
        style={style}
        styles={styles}
        spellCheck={false}
        inputContainer={(children) => (
          <>
            <Popover.Target>{children}</Popover.Target>
            {insertButtons}
          </>
        )}
      />
      <Popover.Dropdown p={0}>
        {dropdownMode === 'variable' && (
          <Stack gap={0} style={{ maxHeight: 300, overflowY: 'auto' }}>
            <Box px="sm" py="xs" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
              <Text size="xs" fw={600} c="dimmed">
                Available Variables
              </Text>
            </Box>
            {filteredVariables.map((variable) => (
              <UnstyledButton
                key={`${variable.sourceNodeId}-${variable.name}`}
                px="sm"
                py="xs"
                onClick={() => insertVariable(variable.name)}
                style={{
                  display: 'block',
                  width: '100%',
                  borderBottom: '1px solid var(--mantine-color-default-border)',
                }}
                styles={{
                  root: {
                    '&:hover': {
                      backgroundColor: 'var(--mantine-color-default-hover)',
                    },
                  },
                }}
              >
                <Group gap="xs" justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap">
                    <IconVariable
                      size={14}
                      style={{
                        color: variable.isSystem ? 'var(--mantine-color-teal-6)' : 'var(--mantine-color-indigo-6)',
                      }}
                    />
                    <Text size="sm" fw={500}>
                      {variable.name}
                    </Text>
                  </Group>
                  <Badge size="xs" variant="light" color={variable.isSystem ? 'teal' : 'gray'}>
                    {variable.sourceWidgetName}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed" mt={2}>
                  {variable.description}
                </Text>
              </UnstyledButton>
            ))}
            {filteredVariables.length === 0 && (
              <Box px="sm" py="md">
                <Text size="sm" c="dimmed" ta="center">
                  No matching variables
                </Text>
              </Box>
            )}
          </Stack>
        )}
        {dropdownMode === 'speechFormat' && (
          <Stack gap={0} style={{ maxHeight: 300, overflowY: 'auto' }}>
            <Box px="sm" py="xs" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
              <Text size="xs" fw={600} c="dimmed">
                Speech Formats
              </Text>
            </Box>
            {groupedFormats.map(({ category, formats }) => (
              <Box key={category}>
                <Box px="sm" py={4} bg="var(--mantine-color-body)">
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                    {categoryLabels[category]}
                  </Text>
                </Box>
                {formats.map((format) => (
                  <UnstyledButton
                    key={format.id}
                    px="sm"
                    py="xs"
                    onClick={() => insertSpeechFormat(format.id)}
                    style={{
                      display: 'block',
                      width: '100%',
                      borderBottom: '1px solid var(--mantine-color-default-border)',
                    }}
                    styles={{
                      root: {
                        '&:hover': {
                          backgroundColor: 'var(--mantine-color-default-hover)',
                        },
                      },
                    }}
                  >
                    <Group gap="xs" wrap="nowrap">
                      <IconSpeakerphone size={14} style={{ color: 'var(--mantine-color-violet-6)' }} />
                      <Text size="sm" fw={500}>
                        /{format.id}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {format.description}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed" mt={2} fs="italic">
                      {format.example}
                    </Text>
                  </UnstyledButton>
                ))}
              </Box>
            ))}
            {filteredFormats.length === 0 && (
              <Box px="sm" py="md">
                <Text size="sm" c="dimmed" ta="center">
                  No matching formats
                </Text>
              </Box>
            )}
          </Stack>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
